/**
 * Conversion jobs — raw LAS/LAZ scans being turned into COPC on AWS Fargate.
 * Polls while any job is queued or running.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';
import type { ConversionJob } from '../../services/api/convertApi';
import { formatBytes } from '../../services/directUpload';

interface Props {
  projectId: string;
  /** Bump to force an immediate refresh (e.g. after queuing new jobs). */
  refreshKey?: number;
  onJobFinished?: () => void;
}

const POLL_MS = 8000;
const ACTIVE = new Set(['queued', 'running']);

const STATUS_LABEL: Record<string, string> = {
  queued: 'Queued', running: 'Converting', succeeded: 'Ready', failed: 'Failed', cancelled: 'Cancelled',
};
const STAGE_LABEL: Record<string, string> = {
  downloading: 'Fetching scan', converting: 'Building COPC', uploading: 'Saving result',
};
const TONE: Record<string, string> = {
  queued: '#8e8e93', running: '#4F7CFF', succeeded: '#30d158', failed: '#ff453a', cancelled: '#8e8e93',
};

function elapsed(job: ConversionJob): string {
  const start = new Date(job.startedAt || job.createdAt).getTime();
  const end = job.finishedAt ? new Date(job.finishedAt).getTime() : Date.now();
  const s = Math.max(0, Math.round((end - start) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

export function ConversionJobsList({ projectId, refreshKey = 0, onJobFinished }: Props) {
  const [jobs, setJobs] = useState<ConversionJob[]>([]);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [, setTick] = useState(0);
  const prevActive = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const r = await api.convert.listJobs(projectId);
      setJobs(r.jobs);
      setConfigured(r.configured);
      setReason(r.reason);
      setError('');
      const nowActive = new Set(r.jobs.filter((j) => ACTIVE.has(j.status)).map((j) => j.id));
      const finished = [...prevActive.current].some((id) => !nowActive.has(id));
      prevActive.current = nowActive;
      if (finished) onJobFinished?.();
    } catch (err: any) {
      setError(err?.message || 'Could not load conversions');
    }
  }, [projectId, onJobFinished]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const anyActive = jobs.some((j) => ACTIVE.has(j.status));
  useEffect(() => {
    if (!anyActive) return;
    const poll = setInterval(load, POLL_MS);
    const tick = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [anyActive, load]);

  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusyId(id);
    try { await fn(); await load(); } catch (err: any) { setError(err?.message || 'Action failed'); } finally { setBusyId(null); }
  };

  if (jobs.length === 0 && configured !== false) return null;

  return (
    <div style={s.wrap}>
      <div style={s.head}>
        <span style={s.title}>Conversions</span>
        {anyActive && <span style={s.live}><span style={s.dot} /> live</span>}
      </div>
      {configured === false && (
        <div style={s.notice}>{reason || 'Point cloud conversion is not configured on this server.'}</div>
      )}
      {error && <div style={{ ...s.notice, color: '#ff453a' }}>{error}</div>}
      {jobs.map((j) => {
        const tone = TONE[j.status] || '#8e8e93';
        const active = ACTIVE.has(j.status);
        return (
          <div key={j.id} style={s.row}>
            <div style={{ ...s.badge, color: tone, borderColor: `${tone}55`, background: `${tone}14` }}>
              {STATUS_LABEL[j.status] || j.status}
            </div>
            <div style={s.main}>
              <div style={s.name} title={j.rawKey}>{j.name || j.rawKey.split('/').pop()}</div>
              <div style={s.meta}>
                {j.inputBytes && <span>{formatBytes(Number(j.inputBytes))} in</span>}
                {j.outputBytes && <span>{formatBytes(Number(j.outputBytes))} out</span>}
                {j.pointCount && <span>{Number(j.pointCount).toLocaleString()} pts</span>}
                {active && j.stage && <span style={{ color: '#8ab0ff' }}>{STAGE_LABEL[j.stage] || j.stage}</span>}
                <span>{active ? `${elapsed(j)} elapsed` : elapsed(j)}</span>
                {j.cpu && active && <span>{j.cpu / 1024} vCPU · {Math.round((j.memory || 0) / 1024)} GB</span>}
              </div>
              {j.errorMessage && <div style={s.err} title={j.errorMessage}>{j.errorMessage}</div>}
            </div>
            <div style={s.actions}>
              {active && <button style={s.btn} disabled={busyId === j.id} onClick={() => act(j.id, () => api.convert.cancel(j.id))}>Cancel</button>}
              {(j.status === 'failed' || j.status === 'cancelled') && <button style={s.btn} disabled={busyId === j.id} onClick={() => act(j.id, () => api.convert.retry(j.id))}>Retry</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  wrap: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { color: '#f5f5f7', fontSize: 13.5, fontWeight: 600 },
  live: { display: 'inline-flex', alignItems: 'center', gap: 6, color: '#8e8e93', fontSize: 12 },
  dot: { width: 7, height: 7, borderRadius: 4, background: '#4F7CFF', boxShadow: '0 0 0 3px rgba(79,124,255,0.25)' },
  notice: { color: '#ffd60a', fontSize: 12.5, lineHeight: 1.4 },
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderTop: '1px solid rgba(255,255,255,0.06)' },
  badge: { flexShrink: 0, fontSize: 11.5, fontWeight: 600, letterSpacing: '0.01em', padding: '3px 9px', borderRadius: 999, border: '1px solid' },
  main: { flex: 1, minWidth: 0 },
  name: { color: '#f5f5f7', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  meta: { display: 'flex', flexWrap: 'wrap', gap: '2px 12px', color: '#8e8e93', fontSize: 12, marginTop: 2, fontVariantNumeric: 'tabular-nums' },
  err: { color: '#ff6b60', fontSize: 12, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  actions: { flexShrink: 0, display: 'flex', gap: 6 },
  btn: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', color: '#f5f5f7', borderRadius: 8, padding: '5px 11px', fontSize: 12.5, cursor: 'pointer' },
};
